import { Badge } from '@chopchop-se/ui';
import { useQuery } from '@tanstack/react-query';
import {
  CLAIM_CREATE_REASONS,
  claimStatusSchema,
  type ClaimListItem,
  type ClaimStatus,
} from '@warehouse/shared';
import { Plus } from 'lucide-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router';

import { DataTable, type DataTableColumn } from '../../../components/DataTable';
import { PageHeader } from '../../../components/PageHeader';
import { useListState } from '../../../hooks/useListState';
import { formatDate, formatDateTime } from '../../../lib/format';
import { useLiveQuery } from '../../../realtime/useLiveQuery';
import { claimKeys, fetchClaims } from '../api/claims-api';
import { ClaimStatusBadge } from '../components/ClaimStatusBadge';
import { useClaimStatuses } from '../hooks/useClaimStatuses';

/** `/orders/claims` — the warehouse's claim inbox. */
export function ClaimsListPage(): ReactNode {
  const { t } = useTranslation(['claims', 'common', 'shell']);
  const navigate = useNavigate();
  const { state, setState, setFilter } = useListState({ defaultSort: 'createdAt', defaultDir: 'desc' });
  const { statuses, label: statusLabel } = useClaimStatuses();

  const query = useQuery({ queryKey: claimKeys.list(state), queryFn: () => fetchClaims(state) });
  useLiveQuery(['claims'], claimKeys.all);

  const columns: DataTableColumn<ClaimListItem>[] = [
    {
      id: 'claimNo',
      header: t('claims:list.columns.claimNo'),
      sortField: 'claimNo',
      cell: (row) => <span className="font-mono text-sm">{row.claimNo}</span>,
    },
    {
      id: 'restaurant',
      header: t('claims:list.columns.restaurant'),
      sortField: 'restaurant',
      cell: (row) => row.restaurantName ?? '—',
    },
    {
      id: 'order',
      header: t('claims:list.columns.order'),
      hideBelow: 'md',
      cell: (row) =>
        row.salesOrderNo ? (
          <span className="flex flex-col">
            <span className="font-mono text-xs">{row.salesOrderNo}</span>
            <span className="text-xs text-ink-muted">{formatDate(row.deliveryDate)}</span>
          </span>
        ) : (
          '—'
        ),
    },
    {
      id: 'reason',
      header: t('claims:list.columns.reason'),
      sortField: 'reason',
      cell: (row) => <Badge tone="neutral">{t(`claims:reason.${row.reason}`)}</Badge>,
    },
    {
      id: 'status',
      header: t('claims:list.columns.status'),
      sortField: 'status',
      cell: (row) => <ClaimStatusBadge status={row.status} />,
    },
    {
      id: 'lines',
      header: t('claims:list.columns.lines'),
      align: 'right',
      hideBelow: 'sm',
      cell: (row) => row.lineCount,
    },
    {
      id: 'photos',
      header: t('claims:list.columns.photos'),
      align: 'right',
      hideBelow: 'lg',
      cell: (row) => row.photoCount,
    },
    {
      id: 'reportedBy',
      header: t('claims:list.columns.reportedBy'),
      hideBelow: 'xl',
      cell: (row) => row.createdByName ?? row.createdByEmail ?? '—',
    },
    {
      id: 'createdAt',
      header: t('claims:list.columns.createdAt'),
      sortField: 'createdAt',
      cell: (row) => formatDateTime(row.createdAt),
    },
  ];

  const currentStatus = String(state.filters?.status ?? '');
  const currentReason = String(state.filters?.reason ?? '');

  return (
    <div>
      <PageHeader
        title={t('claims:title')}
        subtitle={t('claims:subtitle')}
        actions={
          <Link
            to="/orders/claims/new"
            className="inline-flex items-center gap-2 rounded-full bg-brand-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-brand-700"
          >
            <Plus className="size-4" aria-hidden />
            {t('claims:portal.new')}
          </Link>
        }
      />

      <DataTable
        columns={columns}
        rows={query.data?.items ?? []}
        rowKey={(row) => row.id}
        state={state}
        onStateChange={setState}
        total={query.data?.total ?? null}
        loading={query.isLoading}
        error={query.error}
        searchPlaceholder={t('claims:list.searchPlaceholder')}
        emptyTitle={t('claims:list.empty')}
        emptyDescription={t('claims:list.emptyDescription')}
        onRowClick={(row) => {
          void navigate(`/orders/claims/${row.id}`);
        }}
        filters={
          <>
            <label className="flex items-center gap-2 text-sm text-ink-light">
              {t('claims:list.filters.status')}
              <select
                className="rounded-lg border border-surface-border bg-surface px-2 py-1.5 text-sm"
                value={currentStatus}
                onChange={(event) => {
                  setFilter('status', event.target.value || undefined);
                }}
              >
                <option value="">{t('common:common.all')}</option>
                {statuses.map((status) => (
                  <option key={status.key} value={status.key}>
                    {statusLabel(status.key)}
                  </option>
                ))}
              </select>
            </label>

            <label className="flex items-center gap-2 text-sm text-ink-light">
              {t('claims:list.filters.reason')}
              <select
                className="rounded-lg border border-surface-border bg-surface px-2 py-1.5 text-sm"
                value={currentReason}
                onChange={(event) => {
                  setFilter('reason', event.target.value || undefined);
                }}
              >
                <option value="">{t('common:common.all')}</option>
                {CLAIM_CREATE_REASONS.map((reason) => (
                  <option key={reason} value={reason}>
                    {t(`claims:reason.${reason}`)}
                  </option>
                ))}
              </select>
            </label>

            <label className="flex items-center gap-2 text-sm text-ink-light">
              {t('claims:list.filters.from')}
              <input
                type="date"
                className="rounded-lg border border-surface-border bg-surface px-2 py-1.5 text-sm"
                value={String(state.filters?.createdFrom ?? '')}
                onChange={(event) => {
                  setFilter('createdFrom', event.target.value || undefined);
                }}
              />
            </label>

            <label className="flex items-center gap-2 text-sm text-ink-light">
              {t('claims:list.filters.to')}
              <input
                type="date"
                className="rounded-lg border border-surface-border bg-surface px-2 py-1.5 text-sm"
                value={String(state.filters?.createdTo ?? '')}
                onChange={(event) => {
                  setFilter('createdTo', event.target.value || undefined);
                }}
              />
            </label>
          </>
        }
      />
    </div>
  );
}

/** Exported for the status filter's type-safety in tests. */
export const CLAIM_STATUS_VALUES: readonly ClaimStatus[] = claimStatusSchema.options;
